// src/features/payments/components/ContractListForPayment.tsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Contract } from 'src/types/contract.type';
import { useContractMilestones } from 'src/features/contracts/hooks/useContractMilestones';

function toNum(v: any) {
    if (v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function fmtMoney(amount: number, currency?: string | null) {
    const n = Number.isFinite(amount) ? amount : 0;
    const code = (currency ?? '').toUpperCase();
    if (!code) return `${Math.round(n).toLocaleString()}`;
    const locale = code === 'KRW' ? 'ko-KR' : 'en-US';
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(n);
    } catch {
        return `${Math.round(n).toLocaleString()} ${code}`;
    }
}

function displayCompany(c: any) {
    return c?.projectCompany?.company?.name ?? c?.projectCompany?.name ?? c?.partyName ?? '(회사 미지정)';
}

function ContractRowExpanded({ projectId, c }: { projectId: string; c: Contract }) {
    const { data: milestones = [], isLoading } = useContractMilestones(projectId, c.id);

    const totals = useMemo(() => {
        const plan = milestones.reduce((sum, m) => sum + toNum(m.amountPlan), 0);
        const actual = milestones.reduce(
            (sum, m) => sum + (m.payments ?? []).reduce((s2, p) => s2 + toNum((p as any).amount), 0),
            0,
        );
        return { plan, actual, remaining: plan - actual };
    }, [milestones]);

    return (
        <div className="p-4 bg-gray-50 border-t">
            {isLoading ? (
                <div className="text-xs text-gray-500">마일스톤 로딩중…</div>
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <span className="px-2 py-1 rounded-full border bg-white">
                            Plan: <b className="text-gray-800">{fmtMoney(totals.plan, c.currency)}</b>
                        </span>
                        <span className="px-2 py-1 rounded-full border bg-white">
                            Actual: <b className="text-gray-800">{fmtMoney(totals.actual, c.currency)}</b>
                        </span>
                        <span className="px-2 py-1 rounded-full border bg-white">
                            Remaining: <b className="text-gray-800">{fmtMoney(totals.remaining, c.currency)}</b>
                        </span>
                        <span className="text-[11px] text-gray-500">({milestones.length} milestones)</span>
                    </div>

                    <div className="mt-3 overflow-x-auto">
                        <table className="min-w-full text-xs bg-white border rounded-xl overflow-hidden">
                            <thead className="bg-gray-100 text-gray-600">
                                <tr>
                                    <th className="px-3 py-2 text-left">Seq</th>
                                    <th className="px-3 py-2 text-left">Label</th>
                                    <th className="px-3 py-2 text-left">Due</th>
                                    <th className="px-3 py-2 text-right">Plan</th>
                                    <th className="px-3 py-2 text-right">Actual</th>
                                </tr>
                            </thead>
                            <tbody>
                                {milestones.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-3 py-3 text-gray-500">
                                            마일스톤이 없습니다. (Contracts에서 생성)
                                        </td>
                                    </tr>
                                ) : (
                                    milestones.map((m) => {
                                        const actual = (m.payments ?? []).reduce(
                                            (s, p) => s + toNum((p as any).amount),
                                            0,
                                        );
                                        return (
                                            <tr key={m.id} className="border-t">
                                                <td className="px-3 py-2">{m.seq}</td>
                                                <td className="px-3 py-2">{m.label}</td>
                                                <td className="px-3 py-2 text-gray-600">{m.dueDate ?? '—'}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {fmtMoney(toNum(m.amountPlan), c.currency)}
                                                </td>
                                                <td className="px-3 py-2 text-right">{fmtMoney(actual, c.currency)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default function ContractListForPayment({
    projectId,
    title,
    contracts,
    canEdit,
}: {
    projectId: string;
    title: string;
    contracts: Contract[];
    canEdit?: boolean;
}) {
    const nav = useNavigate();
    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const [selectedId, setSelectedId] = useState<string | null>(null);

    function toggle(id: string) {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setSelectedId(id);
    }

    return (
        <div className="rounded-2xl border bg-white overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-medium flex items-center justify-between">
                <div>{title}</div>
                <div className="text-xs text-gray-500">{contracts.length}건</div>
            </div>

            {contracts.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">계약이 없습니다.</div>
            ) : (
                <div className="divide-y">
                    {contracts.map((c) => {
                        const open = openIds.has(c.id);
                        const selected = selectedId === c.id;

                        return (
                            <div key={c.id}>
                                <div
                                    className={[
                                        'px-4 py-3 flex items-center justify-between gap-3 cursor-pointer bg-sky-100/40 hover:bg-sky-200/40',
                                        selected ? 'border-l-4 border-sky-700 bg-sky-50/40' : 'hover:bg-gray-50',
                                    ].join(' ')}
                                    onClick={() => toggle(c.id)}
                                    role="button"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">
                                            {displayCompany(c)} · {c.contractName}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Amount {fmtMoney(toNum((c as any).contractAmount), c.currency)}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            type="button"
                                            className="rounded-xl border px-3 py-1.5 text-xs bg-white hover:bg-gray-50"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                nav(`/projects/${projectId}/payments/contracts/${c.id}`);
                                            }}
                                        >
                                            Execution
                                        </button>

                                        <button
                                            type="button"
                                            className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-gray-100"
                                            aria-label={open ? 'collapse' : 'expand'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggle(c.id);
                                            }}
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                className={`transition-transform ${open ? 'rotate-90' : ''}`}
                                            >
                                                <path d="M8 5l8 7-8 7V5z" fill="currentColor" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {open && <ContractRowExpanded projectId={projectId} c={c} />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
